import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL, createAxiosInstance } from '../../../../api';
import { useDispatch, useSelector } from 'react-redux';
import { logout, syncDatabaseUser } from '../../../../store/main';
import { initProfile, getMyProfile } from '../../../../store/setProfile';
import { toast } from 'react-toastify';

const Confirm = React.forwardRef((props, ref) => {
  const dispatch = useDispatch()
  const { disable } = props
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = React.useState(false);
  const [countdown, setCountdown] = React.useState(0);
  const [isCountdownActive, setIsCountdownActive] = React.useState(false);
  const [countdownStarted, setCountdownStarted] = React.useState(false);
  const countdownRef = React.useRef(null);
  const userId = useSelector((state) => state.main.userId);
  const profileDisabled = useSelector((state) => state.main.profileDisabled);
  const adminDisabled = useSelector((state) => state.main.adminDisabled);

  const handleClickOpen = () => {
    setOpen(true);
  };

  // Expose handleClickOpen via ref
  React.useImperativeHandle(ref, () => ({
    handleClickOpen
  }));

  const handleClose = () => {
    setOpen(false);
    setDeleteConfirmed(false);
    setCountdown(0);
    setIsCountdownActive(false);
    setCountdownStarted(false);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  // Countdown timer effect
  React.useEffect(() => {
    if (isCountdownActive && countdown > 0) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsCountdownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [isCountdownActive, countdown]);


  const handleCancelCountdown = () => {
    setIsCountdownActive(false);
    setCountdown(0);
    setCountdownStarted(false);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  // Effect to trigger actual deletion when countdown reaches 0
  React.useEffect(() => {
    if (countdown === 0 && isCountdownActive === false && countdownStarted) {
      performActualDeletion();
    }
  }, [countdown, isCountdownActive, countdownStarted]);

  const performActualDeletion = async () => {
    setLoading(true);
    try {
      await createAxiosInstance().delete(`${API_URL}/api/auth/me`)
      dispatch(logout())
      dispatch(initProfile())
      toast.success('Account deleted successfully')
      handleClose();
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || 'Deleting account failed')
    }
    setLoading(false);
  };

  const submit = async () => {
    if (disable) {
      setLoading(true);
      try {
        if (profileDisabled) {
          // Enable profile
          const { data } = await createAxiosInstance().post(`${API_URL}/api/auth/enable-profile`)
          dispatch(syncDatabaseUser());
          dispatch(getMyProfile());
          handleClose();
          if (data?.requiresApproval) {
            toast.success('Your profile has been submitted for review.');
          } else {
            toast.success('Your profile is now live.');
          }
        } else {
          // Disable profile
          await createAxiosInstance().post(`${API_URL}/api/auth/disable-profile`)
          dispatch(syncDatabaseUser());
          dispatch(getMyProfile());
          handleClose();
          toast.success('Your profile is now hidden from search.');
        }
      } catch (error) {
        console.log(error);
        toast.error(error.response?.data?.message || 'Action failed');
      }
      setLoading(false);
    } else {
      // For delete account, start the countdown timer (no API call yet)
      if (deleteConfirmed && !isCountdownActive) {
        setCountdown(5);
        setIsCountdownActive(true);
        setCountdownStarted(true);
      }
    }
  }

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {disable
            ? profileDisabled
              ? 'Enable your profile?'
              : 'Disable your profile?'
            : 'Delete your account?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {disable
              ? profileDisabled
                ? 'Enabling your profile will make it visible in search. If you made changes while it was disabled, it may require admin approval.'
                : 'Disabling your profile will hide it from student search, but you can still log in and edit it.'
              : 'Deleting your account is irreversible. Once deleted, you will be unable to log in or recover your account.'}
          </DialogContentText>
          
          {!disable && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteConfirmed}
                    onChange={(e) => setDeleteConfirmed(e.target.checked)}
                    sx={{
                      color: '#712d94',
                      '&.Mui-checked': {
                        color: '#712d94',
                      },
                    }}
                  />
                }
                label="I understand that this action cannot be undone"
              />
              
              {isCountdownActive && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="error.contrastText" align="center">
                    <strong>Account will be deleted in {countdown} seconds...</strong>
                  </Typography>
                  <Typography variant="caption" color="error.contrastText" align="center" display="block" sx={{ mt: 1 }}>
                    You can still cancel this action
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions  sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading || isCountdownActive}
            sx={{
              color: '#712d94',
              borderColor: '#712d94',
              '&:hover': {
                backgroundColor: 'rgba(113, 45, 148, 0.04)',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={isCountdownActive ? handleCancelCountdown : submit} 
            autoFocus 
            disabled={loading || (!disable && !deleteConfirmed)}
            sx={{
              backgroundColor: isCountdownActive ? '#d32f2f' : '#712d94',
              color: 'white',
              borderColor: isCountdownActive ? '#d32f2f' : '#712d94',
              '&:hover': {
                backgroundColor: isCountdownActive ? '#b71c1c' : '#5a2470',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              }
            }}
            variant="contained"
          >
            {loading && (<CircularProgress size="24px" style={{ color: "white", marginRight: '0.50rem' }} />) }
            {isCountdownActive ? (
              <>
                <CloseIcon sx={{ mr: 1 }} />
                Stop Deletion ({countdown}s)
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
});

Confirm.displayName = 'Confirm';

export default Confirm;